/**
 * The item picker's branch-scoped stock-level cache (`CACHE_AUDIT.md` `C-12`).
 *
 * It lives in its own **leaf** module — it imports only a type, never
 * `session.ts`, `workspace.ts` or `itemSearch.ts` — because the session
 * lifecycle has to be able to clear it (`CACHE_AUDIT.md` §5.4) and `session.ts`
 * cannot import `itemSearch.ts`: `itemSearch → workspace → session` already
 * exists, so `session → itemSearch` would close a cycle that evaluates
 * `workspace.ts`'s top-level `watch(..., { immediate: true })` before
 * `session` is initialised.
 *
 * The TTL stays at 30 s (C2.4). Verified: nothing seeds a quantity field from a
 * cached badge — `src/components/ItemOptionRow.vue:26-36,121-141` renders
 * `stockBadge.label` as a `Badge` label only, and no caller reads `item.stock`
 * into a form field. The authoritative stock check is the server's, on submit.
 */

import type { InventoryLevelsResponse } from './types/api'

/** How long a whole-branch stock payload stays usable. */
export const STOCK_TTL_MS = 30_000

const stockCache = new Map<
  string,
  { data: InventoryLevelsResponse; expires: number }
>()

/** The cached payload for `branch`, or null when absent or expired. */
export function cachedStock(branch: string): InventoryLevelsResponse | null {
  const hit = stockCache.get(branch)
  if (!hit) return null
  if (hit.expires <= Date.now()) {
    stockCache.delete(branch)
    return null
  }
  return hit.data
}

/** Cache a branch's stock payload for `STOCK_TTL_MS`. */
export function rememberStock(
  branch: string,
  data: InventoryLevelsResponse,
): void {
  stockCache.set(branch, { data, expires: Date.now() + STOCK_TTL_MS })
}

/**
 * Drop every cached branch payload — on logout, session expiry and a company
 * switch (`CACHE_AUDIT.md` §5.4). A branch switch is a different, narrower
 * clear (C4.2).
 */
export function clearStockCache(): void {
  stockCache.clear()
}
